import { useEffect, useRef } from 'react';
import type { FunilIaStageSlug } from '../funilIaStage';
import type { TrailParticle } from '../engine/types';

interface TrailLayerProps {
  laneRefs: Record<FunilIaStageSlug, React.RefObject<HTMLElement | null>>;
  trails: Record<FunilIaStageSlug, TrailParticle[]>;
}

/**
 * TrailLayer renders particles via direct DOM manipulation for 60fps performance.
 * Returns null from React render — all rendering happens in useEffect.
 */
export function TrailLayer({ laneRefs, trails }: TrailLayerProps): null {
  const containerRefs = useRef<Partial<Record<FunilIaStageSlug, HTMLDivElement>>>({});
  const nodeCountRef = useRef<Partial<Record<FunilIaStageSlug, number>>>({});

  useEffect(() => {
    const lanes: FunilIaStageSlug[] = ['inicio', 'em_negociacao_ia', 'proposta_ia'];

    // Ensure trail containers exist in each lane road
    for (const lane of lanes) {
      const road = laneRefs[lane]?.current;
      if (!road) continue;
      let container = containerRefs.current[lane];
      if (!container || !road.contains(container)) {
        container = document.createElement('div');
        container.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:hidden';
        road.appendChild(container);
        containerRefs.current[lane] = container;
        nodeCountRef.current[lane] = 0;
      }
    }
  });

  useEffect(() => {
    let rafId = 0;
    const tick = () => {
      const now = performance.now();
      const lanes: FunilIaStageSlug[] = ['inicio', 'em_negociacao_ia', 'proposta_ia'];

      for (const lane of lanes) {
        const container = containerRefs.current[lane];
        if (!container) continue;
        const trailArr = trails[lane];
        if (!trailArr) continue;

        // Count-diffing: add/remove DOM nodes to match trail count
        const needed = trailArr.length;
        const current = nodeCountRef.current[lane] ?? 0;

        if (needed > current) {
          for (let i = 0; i < needed - current; i++) {
            const el = document.createElement('div');
            el.className = 'funil-ia-trail';
            container.appendChild(el);
          }
        } else if (needed < current) {
          for (let i = 0; i < current - needed; i++) {
            const last = container.lastChild;
            if (last) container.removeChild(last);
          }
        }
        nodeCountRef.current[lane] = needed;

        // Update positions and opacity
        const children = container.children;
        for (let i = 0; i < trailArr.length && i < children.length; i++) {
          const t = trailArr[i];
          const el = children[i] as HTMLElement;
          const life = Math.max(0, 1 - (now - t.born) / t.life);
          el.style.transform = `translate3d(${t.x - 2}px, ${t.y - 2}px, 0) scale(${0.55 + life * 0.65})`;
          el.style.opacity = `${Math.max(0, life * 0.48)}`;
          el.style.color = t.color;
        }
      }

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [laneRefs, trails]);

  return null;
}
